import { defineStore } from 'pinia'
import { computed, readonly, ref, type Ref } from 'vue'
import { tokenManager } from '@/utils/token'
import {
  clientResetPassword,
  clientSendLoginCode,
  clientSendPasswordResetCode,
  clientSendRegisterCode,
  clientUserLogin,
  clientUserLoginVerify,
  clientUserRegister,
  convertCodeToToken,
  convertTokenToCode,
  getClientUserDetail,
  getTenantUserDetail,
  resetPassword,
  sendLoginCode,
  sendPasswordResetCode,
  sendRegisterCode,
  userLogin,
  userLoginVerify,
  userRegister
} from '@/api/auth'
import type {
  Enterprise,
  LoginCodeRequest,
  LoginRequest,
  LoginResponse,
  LoginVerifyRequest,
  PasswordResetCodeRequest,
  PasswordResetRequest,
  RegisterCodeRequest,
  RegisterRequest
} from '@/types/auth'
import { ControlTowerPath, SourceFrom, UserType } from '@/enums/auth'

/**
 * 用户接口定义
 */
export interface User {
  id: string
  username: string
  email?: string
  phone?: string
  name?: string
  realName?: string
  avatar?: string
  role?: string
  permissions?: string[]
  companyId?: string
  createdAt?: string
  lastLoginAt?: string
  tenantId?: string
  userType?: number
  roleType?: number
}

export const useAuthStore = defineStore('auth', () => {
  // 状态
  const user = ref<User | null>(null)
  const isLoading = ref(false)
  const permissions = ref<readonly string[]>([])
  const enterprises = ref<Enterprise[]>([])
  const userType = ref<UserType>(UserType.TENANT) // 用户类型（从接口获取）
  const sourceFrom = ref<string | null>(null) // 来源标识（home/portal）

  // 计算属性
  const isAuthenticated = computed(() => !!user.value && tokenManager.exists())
  const userRole = computed(() => user.value?.role || 'guest')
  const hasPermissions = computed(() => permissions.value.length > 0)
  const isAdmin = computed(() => user.value?.userType === UserType.TENANT)
  const userInfo = computed(() => user.value)
  const availableEnterprises = computed(() => enterprises.value)


  // 操作方法
  const setUser = (userData: User | null) => {
    user.value = userData
    if (userData) {
      console.log('[TP_AUTH] 用户信息已设置:', userData.username)
    } else {
      console.log('[TP_AUTH] 用户信息已清除')
    }
  }

  const setPermissions = (userPermissions: string[]) => {
    permissions.value = userPermissions
    console.log('[TP_AUTH] 用户权限已设置:', userPermissions)
  }

  const clearPermissions = () => {
    permissions.value = []
    console.log('[TP_AUTH] 用户权限已清除')
  }

  const setLoading = (status: boolean) => {
    isLoading.value = status
  }

  const setSourceFrom = (source: string | null) => {
    sourceFrom.value = source
    console.log('[TP_AUTH] 来源标识已设置:', source)
  }

  const logout = () => {
    console.log('[TP_AUTH] 开始登出流程')

    // 清除token和用户信息
    tokenManager.remove()
    setUser(null)
    clearPermissions()
    enterprises.value = []
    userType.value = UserType.TENANT // 重置为默认值
  }

  /**
   * 处理登录响应的通用逻辑（关键：保存域名信息）
   * @param response 登录响应数据
   */
  const handleLoginResponse = (response: LoginResponse) => {
    // 处理 token
    if (typeof response.token === 'string' && response.token) {
      tokenManager.set(response.token, 7)
      console.log('[TP_AUTH] Token已保存')
    }

    // 设置用户类型
    userType.value = response.userType as UserType

    // 设置用户信息（关键：包含域名信息）
    setUser({
      id: String(response.id),
      username: response.username,
      email: response.email || '',
      phone: response.phone,
      name: response.realName,
      realName: response.realName,
      tenantId: String(response.tenantId),
      userType: response.userType,
      roleType: response.roleType
    })

    // 设置企业信息
    if (response.enterprises) {
      enterprises.value = response.enterprises
    }

    return response
  }

  // ==================== 租户端认证方法 ====================

  /**
   * 用户密码登录
   * @param params 登录参数
   */
  const passwordLogin = async(params: LoginRequest) => {
    try {
      console.log('[TP_AUTH] 密码登录开始:', params.account)
      const response = await userLogin(params)
      return handleLoginResponse(response)
    } catch (error) {
      console.error('[TP_AUTH] 登录失败:', error)
      throw error
    }
  }

  /**
   * 验证码登录
   * @param params 验证码登录参数
   */
  const verifyCodeLogin = async(params: LoginVerifyRequest) => {
    try {
      console.log('[TP_AUTH] 验证码登录开始:', params.account)
      const response = await userLoginVerify(params)
      return handleLoginResponse(response)
    } catch (error) {
      console.error('[TP_AUTH] 登录失败:', error)
      throw error
    }
  }

  /**
   * 登录发送验证码
   * @param params 账号参数
   */
  const sendLoginVerificationCode = async(params: LoginCodeRequest) => {
    try {
      console.log('[TP_AUTH] 发送登录验证码:', params.account)
      const response = await sendLoginCode(params)
      return response
    } catch (error) {
      console.error('[TP_AUTH] 发送验证码失败:', error)
      throw error
    }
  }

  /**
   * 注册发送验证码
   * @param params 手机号参数
   */
  const sendRegisterVerificationCode = async(params: RegisterCodeRequest) => {
    try {
      console.log('[TP_AUTH] 发送注册验证码:', params.phone)
      const response = await sendRegisterCode(params)
      return response
    } catch (error) {
      console.error('[TP_AUTH] 发送验证码失败:', error)
      throw error
    }
  }

  /**
   * 用户注册
   * @param params 注册参数
   */
  const register = async(params: RegisterRequest) => {
    try {
      console.log('[TP_AUTH] 注册开始:', params.username)
      const response = await userRegister(params)
      console.log('[TP_AUTH] 注册成功')
      return response
    } catch (error) {
      console.error('[TP_AUTH] 注册失败:', error)
      throw error
    }
  }

  /**
   * 密码重置发送验证码
   * @param params 账号参数
   */
  const sendPasswordResetVerificationCode = async(params: PasswordResetCodeRequest) => {
    try {
      console.log('[TP_AUTH] 发送密码重置验证码:', params.account)
      return await sendPasswordResetCode(params)
    } catch (error) {
      console.error('[TP_AUTH] 发送验证码失败:', error)
      throw error
    }
  }

  /**
   * 重置密码
   * @param params 重置密码参数
   */
  const resetUserPassword = async(params: PasswordResetRequest) => {
    try {
      console.log('[TP_AUTH] 重置密码开始:', params.account)
      const response = await resetPassword(params)
      console.log('[TP_AUTH] 密码重置成功')
      return response
    } catch (error) {
      console.error('[TP_AUTH] 密码重置失败:', error)
      throw error
    }
  }

  // ==================== 客户端认证方法 ====================

  /**
   * 客户端密码登录
   * @param params 登录参数
   */
  const clientPasswordLogin = async(params: LoginRequest) => {
    try {
      console.log('[TP_AUTH] 客户端密码登录开始:', params.account)
      const response = await clientUserLogin(params)
      return handleLoginResponse(response)
    } catch (error) {
      console.error('[TP_AUTH] 客户端登录失败:', error)
      throw error
    }
  }

  /**
   * 客户端验证码登录
   * @param params 验证码登录参数
   */
  const clientVerifyCodeLogin = async(params: LoginVerifyRequest) => {
    try {
      console.log('[TP_AUTH] 客户端验证码登录开始:', params.account)
      const response = await clientUserLoginVerify(params)
      return handleLoginResponse(response)
    } catch (error) {
      console.error('[TP_AUTH] 客户端登录失败:', error)
      throw error
    }
  }

  /**
   * 客户端登录发送验证码
   * @param params 账号参数
   */
  const clientSendLoginVerificationCode = async(params: LoginCodeRequest) => {
    try {
      console.log('[TP_AUTH] 客户端发送登录验证码:', params.account)
      const response = await clientSendLoginCode(params)
      return response
    } catch (error) {
      console.error('[TP_AUTH] 发送验证码失败:', error)
      throw error
    }
  }

  /**
   * 客户端注册发送验证码
   * @param params 手机号参数
   */
  const clientSendRegisterVerificationCode = async(params: RegisterCodeRequest) => {
    try {
      console.log('[TP_AUTH] 客户端发送注册验证码:', params.phone)
      const response = await clientSendRegisterCode(params)
      return response
    } catch (error) {
      console.error('[TP_AUTH] 发送验证码失败:', error)
      throw error
    }
  }

  /**
   * 客户端用户注册
   * @param params 注册参数
   */
  const clientRegister = async(params: RegisterRequest) => {
    try {
      console.log('[TP_AUTH] 客户端注册开始:', params.username)
      const response = await clientUserRegister(params)
      return handleLoginResponse(response)
    } catch (error) {
      console.error('[TP_AUTH] 客户端注册失败:', error)
      throw error
    }
  }

  /**
   * 客户端密码重置发送验证码
   * @param params 账号参数
   */
  const clientSendPasswordResetVerificationCode = async(params: PasswordResetCodeRequest) => {
    try {
      console.log('[TP_AUTH] 客户端发送密码重置验证码:', params.account)
      const response = await clientSendPasswordResetCode(params)
      return response
    } catch (error) {
      console.error('[TP_AUTH] 发送验证码失败:', error)
      throw error
    }
  }

  /**
   * 客户端重置密码
   * @param params 重置密码参数
   */
  const clientResetUserPassword = async(params: PasswordResetRequest) => {
    try {
      console.log('[TP_AUTH] 客户端重置密码开始:', params.account)
      const response = await clientResetPassword(params)
      console.log('[TP_AUTH] 客户端密码重置成功')
      return response
    } catch (error) {
      console.error('[TP_AUTH] 客户端密码重置失败:', error)
      throw error
    }
  }

  /**
   * Code自动登录
   * @param code 6位验证码
   */
  const autoLoginWithCode = async(code: string) => {
    try {
      console.log('[TP_AUTH] Code自动登录开始:', code)

      // 通过code获取token和用户类型
      const tokenResponse = await convertCodeToToken(code)
      const { token: tokenValue, userType: responseUserType } = tokenResponse

      console.log('[TP_AUTH] 从接口获取用户类型:', responseUserType)

      // 设置用户类型到store（后端返回数字类型）
      userType.value = responseUserType as UserType

      // 设置token到cookies
      tokenManager.set(tokenValue, 7)

      // 根据接口返回的用户类型获取用户信息
      let userDetail: any
      if (responseUserType === UserType.CLIENT) {
        userDetail = await getClientUserDetail()
      } else {
        userDetail = await getTenantUserDetail()
      }

      // 设置用户信息（包含域名信息）
      setUser({
        id: String(userDetail.id),
        username: userDetail.username,
        email: userDetail.email || '',
        phone: userDetail.phone,
        name: userDetail.realName,
        realName: userDetail.realName,
        tenantId: String(userDetail.tenantId),
        userType: userDetail.userType,
        roleType: userDetail.roleType
      })

      // 设置企业信息（如果有）
      if (userDetail.enterprises) {
        enterprises.value = userDetail.enterprises
      }
      return userDetail
    } catch (error) {
      console.error('[TP_AUTH] Code自动登录失败:', error)
      throw error
    }
  }

  /**
   * 获取返回控制塔的Code
   */
  const getReturnCode = async(): Promise<string> => {
    return await convertTokenToCode()
  }

  // ============ 控制塔导航相关方法 ============

  /**
   * 构建控制塔URL的核心逻辑
   */
  const buildControlTowerUrl = ({
                                  targetPath = ControlTowerPath.HOME,
                                  userType: providedUserType,
                                  code,
                                  isLoggedIn = false,
                                  baseUrl = window.location.origin
                                }: {
    targetPath?: ControlTowerPath
    userType?: UserType
    code?: string
    isLoggedIn?: boolean
    baseUrl?:string
  }) => {
    const entryType: EntryType = ENTRY_TYPE || 'portal'
    const effectiveUserType = providedUserType ?? userType.value

    const params = new URLSearchParams()
    params.set('from', entryType)
    if (code) params.set('code', code)

    // 未登录时，根据用户类型和入口类型决定登录页面
    if (!isLoggedIn) {
      const authPath = getAuthPath(effectiveUserType, entryType as SourceFrom)
      return `${baseUrl}${authPath}?${params.toString()}`
    }

    // 已登录时，直接跳转到目标页面
    return `${baseUrl}${targetPath}?${params.toString()}`
  }

  /**
   * 根据用户类型和入口类型获取认证路径
   */
  const getAuthPath = (userType: UserType, entryType: SourceFrom): ControlTowerPath => {
    if (userType === UserType.TENANT) {
      return entryType === SourceFrom.HOME
        ? ControlTowerPath.AUTH_TENANT
        : ControlTowerPath.AUTH_STAFF
    }
    return ControlTowerPath.AUTH_PORTAL
  }

  /**
   * 统一的URL跳转方法
   */
  const navigateToUrl = (url: string) => {
    console.log(`[TP_AUTH] 跳转到控制塔: ${url}`)
    window.location.href = url
  }

  /**
   * 跳转到控制塔
   * @param targetPath 目标页面路径，默认为 DASHBOARD
   * @param userType 用户类型，仅在未登录时需要指定
   * @param baseUrl
   */
  const navigateToControlTower = async(
    targetPath: ControlTowerPath = ControlTowerPath.HOME,
    userType?: UserType,
    baseUrl?: string
  ) => {
    try {
      // 未登录时必须指定用户类型
      if (!isAuthenticated.value && !userType) {
        Message.error('未登录且缺少用户类型，无法确定要跳转的系统')
        return
      }

      // 已登录时自动获取code，未登录时不需要
      const code = isAuthenticated.value ? await getReturnCode() : undefined
      if (code) console.log('[TP_AUTH] 获取到返回Code:', code)

      const url = buildControlTowerUrl({
        targetPath,
        userType,
        code,
        isLoggedIn: isAuthenticated.value,
        baseUrl
      })

      navigateToUrl(url)
    } catch (error) {
      Message.error('跳转到控制塔失败')
      console.error('[TP_AUTH] 跳转到控制塔失败:', error)
    }
  }

  /**
   * 检查用户权限
   * @param permission 权限字符串
   */
  const checkPermission = (permission: string): boolean => {
    if (!user.value) return false
    if (isAdmin.value) return true

    return permissions.value.includes(permission)
  }

  /**
   * 检查用户角色
   * @param role 角色类型
   */
  const hasRole = (role: number | number[]): boolean => {
    if (!user.value) return false

    const roles = Array.isArray(role) ? role : [role]
    return roles.includes(user.value.roleType || 0)
  }

  /**
   * 重置状态
   */
  const resetState = () => {
    user.value = null
    enterprises.value = []
    clearPermissions()

    // 清除 cookies
    tokenManager.remove()
  }

  /**
   * 更新用户信息
   * @param newUserInfo 新的用户信息
   */
  const updateUserInfo = (newUserInfo: Partial<User>) => {
    if (user.value) {
      user.value = { ...user.value, ...newUserInfo }
      console.log('[TP_AUTH] 用户信息已更新')
    }
  }

  return {
    // 状态
    user: user as Readonly<Ref<User | null>>,
    isLoading: readonly(isLoading),
    permissions: readonly(permissions),
    enterprises: readonly(enterprises),
    userType: readonly(userType),
    sourceFrom: readonly(sourceFrom),

    // 计算属性
    isAuthenticated,
    userRole,
    hasPermissions,
    isAdmin,
    userInfo,
    availableEnterprises,

    // 基础操作方法
    logout,
    setLoading,
    setSourceFrom,
    setPermissions,
    clearPermissions,

    // 租户端认证方法
    passwordLogin, // 密码登录
    verifyCodeLogin, // 验证码登录
    sendLoginVerificationCode, // 发送登录验证码
    register, // 用户注册
    sendRegisterVerificationCode, // 发送注册验证码
    sendPasswordResetVerificationCode, // 发送密码重置验证码
    resetUserPassword, // 重置密码
    handleLoginResponse,

    // 客户端认证方法
    clientPasswordLogin, // 客户端密码登录
    clientVerifyCodeLogin, // 客户端验证码登录
    clientSendLoginVerificationCode, // 客户端发送登录验证码
    clientRegister, // 客户端用户注册
    clientSendRegisterVerificationCode, // 客户端发送注册验证码
    clientSendPasswordResetVerificationCode, // 客户端发送密码重置验证码
    clientResetUserPassword, // 客户端重置密码

    // Code登录相关方法（核心功能）
    autoLoginWithCode, // Code自动登录
    getReturnCode, // 获取返回控制塔的Code

    // 控制塔相关方法（根据用户类型自动判断）
    navigateToControlTower,

    // 权限和状态管理方法
    checkPermission,
    hasRole,
    resetState,
    updateUserInfo
  }
}, {
  persist: {
    storage: localStorage,
    paths: ['user', 'enterprises', 'permissions', 'userType', 'sourceFrom'],
    key: 'tp_auth_store'
  }
})
